import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, History, User, Clock, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable Edit History modal.
 *
 * Props:
 *   open:        boolean                        whether the modal is shown
 *   onClose:     () => void                     close callback
 *   title:       string                         shown in the header (e.g. "PO-12345 — Edit History")
 *   historyUrl:  string                         full API path (without base) e.g.
 *                                               "/raw-material/purchase-orders/RMPO-202604-002/history"
 */
export default function EditHistoryModal({ open, onClose, title, historyUrl }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !historyUrl) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}${historyUrl}`)
      .then((res) => setEntries(res.data?.data || []))
      .catch(() => setError('Failed to load edit history'))
      .finally(() => setLoading(false));
  }, [open, historyUrl]);

  if (!open) return null;

  const fmtDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const fmtValue = (v) => {
    if (v === null || v === undefined || v === '') return <em className="text-gray-400">empty</em>;
    if (typeof v === 'object') return <code className="text-xs">{JSON.stringify(v)}</code>;
    const s = String(v);
    return s.length > 120 ? s.slice(0, 120) + '…' : s;
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="edit-history-modal"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="text-blue-600" size={20} />
            <h2 className="text-lg font-bold text-gray-900">{title || 'Edit History'}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            data-testid="edit-history-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {loading && <div className="text-center text-gray-500 py-8">Loading…</div>}
          {error && <div className="text-center text-red-600 py-8">{error}</div>}
          {!loading && !error && entries.length === 0 && (
            <div className="text-center text-gray-500 py-8" data-testid="edit-history-empty">
              No edits recorded yet.
            </div>
          )}
          {!loading && !error && entries.length > 0 && (
            <ol className="space-y-4" data-testid="edit-history-list">
              {entries.map((e) => (
                <li
                  key={e.id}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  data-testid="edit-history-entry"
                >
                  <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                    <span className="flex items-center gap-1">
                      <User size={13} />
                      <span className="font-medium text-gray-800">{e.userName}</span>
                      {e.userEmail ? ` · ${e.userEmail}` : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={13} />
                      {fmtDate(e.timestamp)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mb-2">
                    {e.changeCount} field{e.changeCount === 1 ? '' : 's'} changed
                  </div>
                  <div className="divide-y divide-gray-200">
                    {(e.changes || []).map((c, idx) => (
                      <div key={idx} className="py-2 text-sm grid grid-cols-12 gap-2 items-start">
                        <div className="col-span-3 font-semibold text-gray-700">{c.label}</div>
                        <div className="col-span-4 text-red-700 break-words">
                          {fmtValue(c.oldValue)}
                        </div>
                        <div className="col-span-1 text-center text-gray-400">
                          <ArrowRight size={14} className="inline" />
                        </div>
                        <div className="col-span-4 text-green-700 break-words">
                          {fmtValue(c.newValue)}
                        </div>
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
